import { notificationSnapshot } from "@/cache/notification-snapshot"
import { defineStore } from "pinia"
import { cloneDeep } from "lodash-es"
import { computed, type Ref, ref } from "vue"
import moment, { type Moment } from "moment"
import { OrderRepository } from "@/repositories/order-repository"
import { Order } from "@/models/order.model"
import { Message } from "@/models/message.model"
import { MessageRepository } from "@/repositories/message-repository"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import ModelManager from "@/services/model-manager"
import { BaseModel } from "@/models/base.model"
import type { Instruction } from "@/models/instruction.model"

export const useOrderStore = defineStore("orderStore", () => {
  const orders: Ref<Order[]> = ref([])
  const selectedOrder: Ref<Order | null> = ref(null)
  const showOrderSelection: Ref<boolean> = ref(false)
  const orderWithContextMenu: Ref<Order | null> = ref(null)
  const simpleOrderWithContextMenu: Ref<Order | null> = ref(null)
  const numOrderRequestsInitiated = ref(0)
  const numOrderRequestsCompleted = ref(0)

  const isProcessingOrders = ref(false)
  const processingOrderReferences: Ref<Set<string>> = ref(new Set())

  const instructionDatesAlreadyFetched: Ref<{ [key: string]: boolean }> = ref({})
  const instructionDatesCurrentlyBeingFetched: Ref<{ [key: string]: boolean }> = ref({})

  const multiSelectedOrders = computed<Order[]>(() => {
    return orders.value.filter((order: Order) => order.isMultiSelected)
  })

  const isFetchingOrders = computed(() => {
    return numOrderRequestsCompleted.value !== numOrderRequestsInitiated.value
  })

  useSocketSynchronizationHandlers(Order, orders)

  function markDates(
    collection: Ref<{ [key: string]: boolean }>,
    instructionsDateStart: Moment,
    instructionsDateEnd: Moment,
    bool = true
  ) {
    let currentDate = instructionsDateStart.clone()
    while (currentDate.diff(instructionsDateEnd, "days") <= 0) {
      const dateString = currentDate.format("YYYY-MM-DD")
      collection.value[dateString] = bool
      currentDate = currentDate.add(1, "days") // No need to clone here again as add() returns a new instance
    }
  }

  function onInitiateRequestForDateRange(
    instructionsDateStart: Moment,
    instructionsDateEnd: Moment
  ) {
    markDates(instructionDatesAlreadyFetched, instructionsDateStart, instructionsDateEnd)
    markDates(instructionDatesCurrentlyBeingFetched, instructionsDateStart, instructionsDateEnd)
    numOrderRequestsInitiated.value++
  }

  function onRequestForDateRangeCompleted(
    instructionsDateStart: Moment,
    instructionsDateEnd: Moment
  ) {
    markDates(
      instructionDatesCurrentlyBeingFetched,
      instructionsDateStart,
      instructionsDateEnd,
      false
    )
    numOrderRequestsCompleted.value++
  }

  function dateIsAlreadyFetched(date: Moment) {
    return instructionDatesAlreadyFetched.value[date.format("YYYY-MM-DD")]
  }

  function dateIsCurrentlyBeingFetched(date: Moment) {
    return instructionDatesCurrentlyBeingFetched.value[date.format("YYYY-MM-DD")]
  }

  async function pushNonExistentOrders(ordrs: Order[]) {
    const newOrders: Order[] = []
    for (const order of ordrs) {
      if (!ModelManager.getInstance().exists(order.id!)) {
        const newOrder = ModelManager.getInstance().create(Order, order)
        if (newOrder) {
          // create() returns null for locally deleted orders.
          newOrders.push(newOrder)
        }
      } else if (notificationSnapshot.wasHydrated) {
        newOrders.push(ModelManager.getInstance().update(Order, order))
      }
    }
    // Await any pending managed field resolutions before proceeding.
    await BaseModel.whenFullyResolved(newOrders)
    const existingOrderIndices = new Map(orders.value.map((order, index) => [order.id, index]))
    for (const newOrder of newOrders) {
      // A socket update for a not-yet-fetched order parks the model and pushes a
      // placeholder instance into the list (see useSocketSynchronizationHandlers),
      // which exists() does not see. Replace such an entry with the managed instance
      // instead of pushing a duplicate card (PNTS-3077).
      const existingIndex = existingOrderIndices.get(newOrder.id)
      if (existingIndex === undefined) {
        existingOrderIndices.set(newOrder.id, orders.value.length)
        orders.value.push(newOrder)
      } else {
        orders.value[existingIndex] = newOrder
      }
    }
  }

  async function saveOrder(order: Order) {
    isProcessingOrders.value = true
    processingOrderReferences.value.add(order.reference)
    try {
      const result = await new OrderRepository().create(order)
      const newOrder = ModelManager.getInstance().update(Order, result.data)
      orders.value.push(newOrder!)
      return newOrder
    } finally {
      processingOrderReferences.value.delete(order.reference)
      isProcessingOrders.value = processingOrderReferences.value.size > 0
    }
  }

  /**
   * Adds a message to an order.
   * @param message - The message to add.
   * @param order - Optional: The order to add the message to, otherwise the selected order is used.
   */
  async function addMessageToOrder(message: Message, order?: Order) {
    order = order || selectedOrder.value!
    message.order = order.id
    const result = await new MessageRepository().create(message)
    const newMessage = ModelManager.getInstance().updateOrCreate(Message, result.data)
    if (newMessage) {
      order.messages.push(newMessage)
    }
    /* Adding a message to order should cause an order to become unprepared. */
    order!.isPrepared = false
    updateOrder(order!).then() // Don't wait for it to finish
  }

  async function updateOrder(order: Order) {
    order.localUpdatedAt = moment() // So that order is moved to the top of the list

    const orderCopy = cloneDeep(order)
    await new OrderRepository().update(order.id, orderCopy)
  }

  function multiSelectAllOrders() {
    for (const order of orders.value) {
      order.isMultiSelected = true
    }
  }

  function multiDeselectAllOrders() {
    for (const order of orders.value) {
      order.isMultiSelected = false
    }
  }

  async function mergeOrders(ordrs: Order[]): Promise<Order | null> {
    const ordrsCopy = cloneDeep(ordrs)
    const result = await new OrderRepository().mergeOrders(ordrsCopy)
    const mergedOrder = ModelManager.getInstance().updateOrCreate(Order, result.data)
    for (const ordr of ordrs) {
      deleteOrderLocally(ordr)
    }
    if (mergedOrder) {
      orders.value.push(mergedOrder)
    }
    return mergedOrder
  }

  async function deleteOrders(ordrs: Order[]) {
    isProcessingOrders.value = true
    for (const ordr of ordrs) {
      processingOrderReferences.value.add(ordr.reference)
      await new OrderRepository().delete(ordr.id)
      deleteOrderLocally(ordr)
    }
    processingOrderReferences.value.clear()
    isProcessingOrders.value = false
  }

  function getAllFiles(order: Order) {
    const files = []
    for (const msg of order.messages) {
      if (msg.fileBucket.fileRelations) {
        files.push(...msg.fileBucket.fileRelations)
      }
    }
    return files
  }

  function findOrderByInstruction(instruction: Instruction) {
    for (const order of orders.value) {
      for (const instr of order.instructions) {
        if (instruction.id === instr.id) {
          return order
        }
      }
    }
    return null
  }

  function deleteOrderLocally(order: Order) {
    const match = orders.value.filter((ordr: any) => ordr.id === order.id)[0]
    const index = orders.value.indexOf(match)
    if (index !== -1) {
      orders.value.splice(index, 1)
    }

    if (order.id === selectedOrder.value?.id) {
      selectedOrder.value = null
    }
  }

  return {
    orders,
    selectedOrder,
    showOrderSelection,
    orderWithContextMenu,
    simpleOrderWithContextMenu,
    multiSelectedOrders,
    isFetchingOrders,
    isProcessingOrders,
    processingOrderReferences,
    instructionDatesCurrentlyBeingFetched,
    numOrderRequestsInitiated,
    numOrderRequestsCompleted,
    saveOrder,
    addMessageToOrder,
    multiSelectAllOrders,
    multiDeselectAllOrders,
    mergeOrders,
    deleteOrders,
    getAllFiles,
    findOrderByInstruction,
    updateOrder,
    pushNonExistentOrders,
    onInitiateRequestForDateRange,
    onRequestForDateRangeCompleted,
    dateIsAlreadyFetched,
    dateIsCurrentlyBeingFetched
  }
})
